import { createPortal } from 'react-dom';

import ActionPanel from '../components/ActionPanel.jsx';
import {
  CloseIcon,
  InfoIcon,
  ScaleIcon,
  SettingsIcon,
} from '../components/icons.jsx';
import useModalDismiss from '../components/useModalDismiss.js';
import SignOutButton from '../auth/SignOutButton.jsx';
import FlagSet from '../flags/FlagSet.jsx';
import { ACCOUNT_PATH, HELP_PATH, LEGAL_PATH } from './paths.js';

// The burger menu's panel — the web's take on the app's Menu screen (screens/Menu/
// Menu.js): its "Menu" / "Your account preferences" header, one action panel per
// section the web offers (Account, Help — "Support & FAQs" — and Legal), the pilot /
// debug tick boxes for an eligible account (FlagSet, nothing for anyone else),
// and Logout pinned to the bottom, where the app keeps it. On the web it is a
// drawer sliding
// in from the burger's edge over the dimmed page rather than a page of its own, so
// the user keeps their place and a tap outside, Escape or the cross puts them
// straight back.
//
// Same chrome contract as ModalShell: a focus-managed dialog (focus-on-open, Tab
// trap, Escape, focus restore — all in useModalDismiss), portaled to <body> so the
// app root can be made inert behind it. Rendered by MenuProvider (above the
// screens, see there), not by the button that opens it. Choosing a section
// navigates away and the route change closes the drawer; the dismiss hook's focus restore
// then falls back to the new screen's heading, its opener having gone with the
// Property screen.
export default function MenuDrawer({ onClose }) {
  const dialogRef = useModalDismiss(onClose);

  return createPortal(
    <div
      className="menu-drawer__backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="menu-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-menu-title"
        tabIndex={-1}
        ref={dialogRef}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="menu-drawer__header">
          <div>
            <h2 id="app-menu-title" className="menu-drawer__title">
              Menu
            </h2>
            <p className="menu-drawer__sub">Your account preferences</p>
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label="Close menu"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </header>
        <div className="menu-drawer__items">
          <nav className="menu-drawer__nav">
            {/* The app's Account entry, ahead of Help as in the app's Menu. */}
            <ActionPanel
              to={ACCOUNT_PATH}
              icon={<SettingsIcon />}
              title="Account"
              sub="Manage account settings"
            />
            <ActionPanel
              to={HELP_PATH}
              icon={<InfoIcon />}
              title="Help"
              sub="Support & FAQs"
            />
            {/* The app keeps its legal documents on sign-up and in the
                terms-review overlay, neither of which web has, so the menu is
                where they live here. Never gated behind a flag. */}
            <ActionPanel
              to={LEGAL_PATH}
              icon={<ScaleIcon />}
              title="Legal"
              sub="Terms & privacy"
            />
          </nav>
          {/* The app's FlagSet sits under the menu's panels. */}
          <FlagSet />
        </div>
        <div className="menu-drawer__lower">
          <SignOutButton />
        </div>
      </div>
    </div>,
    document.body,
  );
}
